/**
 * Founder-facing agreement readiness for Feature Handoff.
 * Ready only when product approved and engineer accepted.
 */

/**
 * @param {{
 *   preparing?: boolean,
 *   hasDraft?: boolean,
 *   promiseCount?: number,
 *   contractState?: string|null,
 *   hasEngAck?: boolean,
 *   hasEngineeringRejection?: boolean,
 *   openQuestionCount?: number,
 *   requirementsConflict?: boolean,
 * }} input
 */
export function deriveHandoffAgreementStatus(input = {}) {
  const preparing = Boolean(input.preparing);
  const hasDraft = Boolean(input.hasDraft);
  const promiseCount = Number(input.promiseCount) || 0;
  const contractState = input.contractState || null;
  const hasEngAck = Boolean(input.hasEngAck);
  const hasEngineeringRejection = Boolean(input.hasEngineeringRejection);
  const openQuestionCount = Number(input.openQuestionCount) || 0;

  if (preparing) {
    return {
      key: "preparing",
      ready: false,
      label: "Not ready",
      headline: "Still preparing the handoff",
      detail: "Promises are not ready to agree on yet.",
    };
  }

  if (!hasDraft || promiseCount === 0) {
    return {
      key: "no_promises",
      ready: false,
      label: "Not ready",
      headline: "No agreed promises yet",
      detail: "Extract or add at least one promise before either party can agree.",
    };
  }

  // Ahead of every remaining state, including agreement itself. Both parties may
  // genuinely have agreed — on requirements that turned out to be impossible to
  // satisfy together, which is a thing to withdraw rather than to report as ready.
  // Ahead of the engineer's change request too, because revising to satisfy that
  // complaint would not settle which of the conflicting requirements holds.
  if (input.requirementsConflict) {
    return {
      key: "requirements_conflict",
      ready: false,
      label: "Not ready",
      headline:
        hasEngAck || contractState === "engineering_acknowledged"
          ? "The agreement no longer holds"
          : "These requirements conflict with each other",
      detail:
        hasEngAck || contractState === "engineering_acknowledged"
          ? "Two of the agreed promises ask for incompatible things. Product has to decide which one holds before delivery work can rely on this."
          : "Two of these promises ask for incompatible things. Product has to decide which one holds before either party can agree.",
    };
  }

  if (hasEngineeringRejection) {
    return {
      key: "engineer_rejected",
      ready: false,
      label: "Not ready",
      headline: "Engineer requested changes",
      detail: "Revise the promises, then product must approve again and the engineer must accept.",
    };
  }

  if (hasEngAck || contractState === "engineering_acknowledged") {
    return {
      key: "agreed",
      ready: true,
      label: "Ready",
      headline: "Both parties agreed on the promises",
      detail:
        openQuestionCount > 0
          ? "Product approved and engineer accepted. Some clarifications remain as warnings."
          : "Product approved and engineer accepted. This handoff is ready for delivery work.",
    };
  }

  if (contractState === "product_approved") {
    return {
      key: "awaiting_engineer",
      ready: false,
      label: "Not ready",
      headline: "Waiting for the engineer to accept",
      detail: "Product approved the promises. The engineer still needs to accept or request changes.",
    };
  }

  return {
    key: "awaiting_product",
    ready: false,
    label: "Not ready",
    headline: "Waiting for product approval",
    detail: `${promiseCount} promise${
      promiseCount === 1 ? "" : "s"
    } drafted — not agreed until product approves and the engineer accepts.`,
  };
}

/**
 * Compact delivery summary for Work Brief technical overview + Build Prompt readiness.
 * Guidance for the UI — server Build Prompt loads the tip Intent Contract authoritatively.
 *
 * @param {{
 *   ready?: boolean,
 *   state?: string|null,
 *   contractId?: string|null,
 *   versionId?: string|null,
 *   promises?: string[],
 *   clarifications?: Array<{ question: string, answer: string, kind?: string }>,
 *   notes?: string[],
 *   openQuestions?: string[],
 * }} input
 */
export function buildHandoffDeliverySummary(input = {}) {
  const promises = (input.promises || []).map((p) => String(p || "").trim()).filter(Boolean);
  const clarifications = (input.clarifications || [])
    .map((c) => ({
      question: String(c?.question || "").trim(),
      answer: String(c?.answer || "").trim(),
      kind: c?.kind || "answered",
    }))
    .filter((c) => c.question || c.answer);
  const notes = (input.notes || []).map((n) => String(n || "").trim()).filter(Boolean);
  const openQuestions = (input.openQuestions || [])
    .map((q) => String(q || "").trim())
    .filter(Boolean);

  return {
    ready: Boolean(input.ready),
    state: input.state || null,
    contractId: input.contractId || null,
    versionId: input.versionId || null,
    promiseCount: promises.length,
    clarificationCount: clarifications.length,
    openQuestionCount: openQuestions.length,
    promises,
    clarifications,
    notes,
    openQuestions,
  };
}

/**
 * Suggested human-editable promise drafts for common quality / product areas.
 * Not contractual until the user adds and saves them.
 *
 * @param {{ requestText?: string, existingPromiseTexts?: string[] }} input
 * @returns {Array<{ id: string, area: string, suggestedPromise: string }>}
 */
export function suggestCoveragePromiseDrafts(input = {}) {
  const request = String(input.requestText || "").trim();
  const existing = (input.existingPromiseTexts || [])
    .map((t) => String(t || "").toLowerCase())
    .filter(Boolean);
  const alreadyCovered = (re) => existing.some((t) => re.test(t));

  const out = [];
  const push = (id, area, suggestedPromise, coverRe) => {
    if (coverRe && alreadyCovered(coverRe)) return;
    if (existing.some((t) => t.includes(suggestedPromise.toLowerCase().slice(0, 40)))) {
      return;
    }
    out.push({ id, area, suggestedPromise });
  };

  const timeoutish = /\btimeout|too long|slow request|fastify|forever\b/i.test(request);
  const appish = /\bapp|ui|edit|render|repo|dashboard|panel\b/i.test(request);

  push(
    "testing",
    "Testing",
    timeoutish
      ? "I want automated tests that prove slow requests are stopped and a timeout error is returned."
      : "I want automated tests that cover the main success path and the important failure cases for this change.",
    /\btest|automated tests|coverage\b/i
  );

  push(
    "scalability",
    "Scalability",
    timeoutish
      ? "I want the timeout behavior to stay safe under concurrent requests without letting work run forever."
      : "I want this change to remain safe as usage grows (no unbounded work or unbounded growth in cost).",
    /\bscalab|concurrent|unbounded\b/i
  );

  push(
    "editability",
    "Editability (both parties)",
    "I want both product and engineering to be able to revise the handoff promises and notes before final acceptance.",
    /\brevise the handoff|both product and engineering\b/i
  );

  push(
    "notes_transparency",
    "Notes / transparency",
    "I want clarifications and engineer notes to stay visible on the handoff so both parties see the same agreement.",
    /\bclarifications and engineer notes|same agreement\b/i
  );

  if (appish || timeoutish) {
    push(
      "renderrepo_ux",
      "In-app clarity",
      "I want the RenderRepo handoff UI to show clearly whether promises are drafted, waiting on a party, or fully agreed.",
      /\brenderrepo handoff ui|fully agreed\b/i
    );
  }

  return out;
}
